import React, { useEffect, useState } from "react";
import { Row, Col, Form, Card, ListGroup, Badge, Alert, InputGroup, Spinner, Table } from "react-bootstrap";
import { FaBook, FaSearch } from "react-icons/fa";
import { runbookService } from "../../services/runbook.service";
import { permissionGroupRunbookService } from "../../services/permissionGroupRunbook.service";
import { userPermissionGroupService } from "../../services/userPermissionGroup.service";
import { userService } from "../../services/user.service";
import { permissionGroupService } from "../../services/permissionGroup.service";

function RunbookAccessLookup({ user }) {
  const [runbooks, setRunbooks] = useState([]);
  const [selectedRunbook, setSelectedRunbook] = useState(null);
  const [accessGroups, setAccessGroups] = useState([]);
  const [groupUsers, setGroupUsers] = useState({});
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingAccess, setLoadingAccess] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredRunbooks, setFilteredRunbooks] = useState([]);

  useEffect(() => {
    if (user?.clientId && user?.clientGuid) {
      loadData();
    }
  }, [user?.clientId, user?.clientGuid]);

  useEffect(() => {
    if (searchTerm) {
      const filtered = runbooks.filter(
        (r) =>
          r.runbookName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          r.runbookDescription?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          r.runbookId?.toString().includes(searchTerm)
      );
      setFilteredRunbooks(filtered);
    } else {
      setFilteredRunbooks(runbooks);
    }
  }, [searchTerm, runbooks]);

  const loadData = async () => {
    try {
      setLoading(true);
      const params = {
        clientId: user.clientId,
        clientGuid: user.clientGuid,
        max_results: 1000,
        current_page: 0,
      };

      // Load runbooks
      const runbooksResp = await runbookService.get(params);
      const runbooksData = runbooksResp.data?.data || runbooksResp.data || runbooksResp;
      const runbooksArray = Array.isArray(runbooksData) ? runbooksData : [];
      setRunbooks(runbooksArray);
      setFilteredRunbooks(runbooksArray);

      // Load all users for name lookups
      const userParams = { clientId: user.clientId, clientGuid: user.clientGuid };
      const usersResp = await userService.get(userParams);
      // Handle paginated response: response.data.data contains the actual array
      const usersData = usersResp.data?.data || usersResp.data || usersResp;
      const usersArray = Array.isArray(usersData) ? usersData : [];
      setAllUsers(usersArray);

      setError(null);
    } catch (err) {
      setError("Failed to load data");
      setRunbooks([]);
      setFilteredRunbooks([]);
      setAllUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRunbookSelect = async (selectedRunbookId) => {
    if (!selectedRunbookId) {
      setSelectedRunbook(null);
      setAccessGroups([]);
      setGroupUsers({});
      return;
    }

    try {
      setLoadingAccess(true);
      // Runbook IDs can be strings like "RB2535", so don't parse to int
      const runbook = runbooks.find((r) => r.runbookId === selectedRunbookId || r.runbookId.toString() === selectedRunbookId);
      setSelectedRunbook(runbook);

      // Get group mappings that have access to this runbook (just the groupIds)
      const groupMappingsResp = await permissionGroupRunbookService.getGroupsForRunbook(selectedRunbookId);
      const groupMappings = groupMappingsResp.data || groupMappingsResp;

      // Extract groupIds
      const accessGroupIds = groupMappings.map(m => m.groupId);

      // Fetch all groups for this client to get full details
      const allGroupsResp = await permissionGroupService.getAll({
        clientId: user.clientId,
        clientGuid: user.clientGuid
      });
      const allGroups = allGroupsResp.data || allGroupsResp;

      // Filter to just the groups that have access to this runbook
      const groupsWithDetails = allGroups.filter(g => accessGroupIds.includes(g.groupId));
      setAccessGroups(groupsWithDetails);

      // Get users in each group
      const usersMap = {};
      for (const group of groupsWithDetails) {
        const usersResp = await userPermissionGroupService.getUsersInGroup(group.groupId);
        const users = usersResp.data || usersResp;
        usersMap[group.groupId] = users;
      }
      setGroupUsers(usersMap);
      setError(null);
    } catch (err) {
      setError("Failed to load runbook access information");
    } finally {
      setLoadingAccess(false);
    }
  };

  // Get all unique users who have access
  const getAllUsersWithAccess = () => {
    const userIds = new Set();
    Object.values(groupUsers).forEach((users) => {
      users.forEach((u) => userIds.add(u.userId));
    });

    // Get full user details
    return Array.from(userIds).map((userId) => {
      return allUsers.find((u) => u.userId === userId);
    }).filter(Boolean);
  };

  const getUserDetails = (userId) => {
    return allUsers.find((u) => u.userId === userId);
  };

  // Color palette for permission groups
  const groupColors = [
    'primary',    // Blue
    'success',    // Green
    'warning',    // Yellow/Orange
    'danger',     // Red
    'info',       // Light Blue
    'secondary',  // Gray
    'dark',       // Dark Gray
  ];

  // Get consistent color for a group based on its index
  const getGroupColor = (groupId) => {
    const index = accessGroups.findIndex(g => g.groupId === groupId);
    return groupColors[index % groupColors.length];
  };

  if (loading) return <div>Loading...</div>;

  return (
    <>
      {error && <Alert variant="danger">{error}</Alert>}

      <Row>
        <Col md={4}>
          <Card>
            <Card.Body>
              <h5 className="mb-3">
                <FaBook className="me-2" />
                Select Runbook
              </h5>

              <InputGroup className="mb-3">
                <InputGroup.Text>
                  <FaSearch />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search runbooks..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>

              <Form.Select
                size="lg"
                value={selectedRunbook?.runbookId || ""}
                onChange={(e) => handleRunbookSelect(e.target.value)}
              >
                <option value="">-- Select a runbook --</option>
                {Array.isArray(filteredRunbooks) && filteredRunbooks.map((r) => (
                  <option key={r.runbookId} value={r.runbookId}>
                    {r.runbookName}
                  </option>
                ))}
              </Form.Select>

              {selectedRunbook && (
                <div className="mt-3">
                  <h6>Selected Runbook:</h6>
                  <p className="mb-1">
                    <strong>{selectedRunbook.runbookName}</strong>
                  </p>
                  {selectedRunbook.runbookDescription && (
                    <p className="text-muted small mb-0">{selectedRunbook.runbookDescription}</p>
                  )}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col md={8}>
          {!selectedRunbook ? (
            <Alert variant="info">
              <FaBook className="me-2" />
              Select a runbook to see which users have access to it through permission groups.
            </Alert>
          ) : loadingAccess ? (
            <div className="text-center p-5">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
              <p className="mt-3">Loading access information...</p>
            </div>
          ) : (
            <>
              <Card className="mb-3">
                <Card.Body>
                  <h5>Permission Groups with Access</h5>
                  {accessGroups.length === 0 ? (
                    <Alert variant="warning">
                      This runbook is not assigned to any permission groups. No users can access it.
                    </Alert>
                  ) : (
                    <ListGroup>
                      {accessGroups.map((group) => (
                        <ListGroup.Item key={group.groupId}>
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <Badge bg={getGroupColor(group.groupId)} className="me-2">
                                {group.groupName}
                              </Badge>
                              {group.groupDescription && (
                                <div className="text-muted small mt-1">{group.groupDescription}</div>
                              )}
                            </div>
                            <Badge bg={group.active ? "success" : "secondary"}>
                              {group.active ? "Active" : "Inactive"}
                            </Badge>
                          </div>
                          {groupUsers[group.groupId] && (
                            <div className="mt-2">
                              <small className="text-muted">
                                {groupUsers[group.groupId].length} user(s) in this group
                              </small>
                            </div>
                          )}
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  )}
                </Card.Body>
              </Card>

              <Card>
                <Card.Body>
                  <h5>Users with Access</h5>
                  <p className="text-muted small">
                    All unique users who can access this runbook through their group memberships:
                  </p>

                  {getAllUsersWithAccess().length === 0 ? (
                    <Alert variant="warning">
                      No users have access to this runbook. The groups may be empty or inactive.
                    </Alert>
                  ) : (
                    <>
                      <Table striped bordered hover responsive>
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Groups</th>
                          </tr>
                        </thead>
                        <tbody>
                          {getAllUsersWithAccess().map((user) => {
                            // Find which groups this user is in (that have access to this runbook)
                            const userGroups = accessGroups.filter((group) =>
                              groupUsers[group.groupId]?.some((u) => u.userId === user.userId)
                            );

                            return (
                              <tr key={user.userId}>
                                <td>
                                  {user.firstName} {user.lastName}
                                </td>
                                <td>{user.email}</td>
                                <td>
                                  {userGroups.map((group) => (
                                    <Badge
                                      key={group.groupId}
                                      bg={getGroupColor(group.groupId)}
                                      className="me-1"
                                    >
                                      {group.groupName}
                                    </Badge>
                                  ))}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>

                      <div className="mt-3 p-3 bg-light rounded">
                        <strong>Total: </strong>
                        {getAllUsersWithAccess().length} unique user(s) with access
                      </div>
                    </>
                  )}
                </Card.Body>
              </Card>
            </>
          )}
        </Col>
      </Row>
    </>
  );
}

export default RunbookAccessLookup;
